import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, fromEvent } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  static readonly BACKGROUND_RELOAD_THRESHOLD_MS = 30 * 60 * 1000;

  private readonly swUpdate = inject(SwUpdate);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly updateReady = signal(false);
  private readonly noticeDismissed = signal(false);
  private hiddenAt: number | null = null;

  readonly showUpdateNotice = computed(
    () => this.updateReady() && !this.noticeDismissed(),
  );

  start(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.updateReady.set(true));

    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reload());

    fromEvent(this.doc, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onVisibilityChange());

    if (this.doc.defaultView) {
      fromEvent(this.doc.defaultView, 'focus')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => void this.swUpdate.checkForUpdate());
    }
  }

  applyUpdate(): void {
    void this.swUpdate
      .activateUpdate()
      .catch(() => false)
      .then(() => this.reload());
  }

  dismissNotice(): void {
    this.noticeDismissed.set(true);
  }

  private onVisibilityChange(): void {
    if (this.doc.visibilityState === 'hidden') {
      this.hiddenAt = Date.now();
      return;
    }

    const awayForMs = this.hiddenAt === null ? 0 : Date.now() - this.hiddenAt;
    this.hiddenAt = null;

    if (
      this.updateReady() &&
      awayForMs >= AppUpdateService.BACKGROUND_RELOAD_THRESHOLD_MS
    ) {
      this.applyUpdate();
      return;
    }

    void this.swUpdate.checkForUpdate();
  }

  private reload(): void {
    this.doc.location.reload();
  }
}
